import { BALANCE } from '../config/balance';
import { clamp, sectorIndex } from '../core/vec';
import type { DamageSource, DeathCauseId } from '../core/types';
import type { Enemy } from './enemies';
import type { Gem } from './gems';

export interface DamageRecord {
  t: number;
  source: DamageSource;
  amount: number;
  hpRatio: number;
}

export interface Snapshot {
  t: number;
  hpRatio: number;
  sectors: number[];
  totalNear: number;
  gemDot: number; // 移动方向与最近宝石方向的点积；无近宝石时为 0
  gemDist: number;
  /** 最近精英 / Boss 距离与靠近点积（贪刀判定）。 */
  threatDist: number;
  threatDot: number;
}

export interface DeathResult {
  cause: DeathCauseId;
  label: string;
  confidence: number;
  factors: string[];
}

const CAUSE_LABEL: Record<DeathCauseId, string> = {
  BOMBED: '被自爆菇带走',
  CHARGED: '没躲过冲锋犬的冲锋',
  SHIELD_CHIP: '护盾耗尽后的裸伤',
  GREED: '死于贪刀',
  SURROUNDED: '走位失误 · 被包围',
  ATTRITION: '弹尽粮绝 · 慢性消耗',
  ELITE_COUNTER: '被精英词条克制',
  BOSS_SKILL: 'Boss 技能命中',
  TIMEOUT: '时间耗尽',
};

export class DeathLog {
  private damages: DamageRecord[] = [];
  private snaps: Snapshot[] = [];
  private shieldBreaks: number[] = [];

  recordDamage(t: number, source: DamageSource, amount: number, hpRatio: number): void {
    this.damages.push({ t, source, amount, hpRatio });
    this.prune(t);
  }

  recordShieldBreak(t: number): void {
    this.shieldBreaks.push(t);
    this.prune(t);
  }

  /** 周期性快照：8 扇区近敌分布 + 最近宝石与移动意图。 */
  recordSnapshot(
    t: number,
    hpRatio: number,
    px: number,
    py: number,
    moveX: number,
    moveY: number,
    enemies: readonly Enemy[],
    gems: readonly Gem[],
  ): void {
    const d = BALANCE.death;
    const sectors = new Array<number>(d.sectorCount).fill(0);
    let totalNear = 0;
    for (const e of enemies) {
      if (!e.active) continue;
      const dx = e.x - px;
      const dy = e.y - py;
      if (Math.hypot(dx, dy) <= d.surroundRadius) {
        totalNear++;
        sectors[sectorIndex(dx, dy, d.sectorCount)]++;
      }
    }

    let gemDot = 0;
    let gemDist = Infinity;
    const ml = Math.hypot(moveX, moveY);
    if (ml > 0.1) {
      for (const g of gems) {
        if (!g.active || g.flying) continue;
        const gd = Math.hypot(g.x - px, g.y - py);
        if (gd < d.greedGemDist && gd < gemDist) {
          gemDist = gd;
          gemDot = ((g.x - px) / gd) * (moveX / ml) + ((g.y - py) / gd) * (moveY / ml);
        }
      }
    }

    let threatDist = Infinity;
    let threatDot = 0;
    for (const e of enemies) {
      if (!e.active) continue;
      const isThreat = e.kind === 'elite' || e.kind === 'boss1' || e.kind === 'boss2';
      if (!isThreat) continue;
      const ed = Math.hypot(e.x - px, e.y - py);
      if (ed < threatDist) {
        threatDist = ed;
        if (ml > 0.1) {
          threatDot = ((e.x - px) / ed) * (moveX / ml) + ((e.y - py) / ed) * (moveY / ml);
        }
      }
    }

    this.snaps.push({
      t, hpRatio, sectors, totalNear, gemDot, gemDist, threatDist, threatDot,
    });
    this.prune(t);
  }

  /** 死亡瞬间按优先级判定主因。 */
  attribute(t: number, lethal: DamageRecord | null): DeathResult {
    const factors = this.buildFactors(lethal);

    if (lethal?.source.type === 'explosion') {
      return this.result('BOMBED', 0.98, factors);
    }
    if (lethal?.source.type === 'charge') {
      return this.result('CHARGED', 0.95, factors);
    }
    if (this.wasBossHit(lethal)) {
      return this.result('BOSS_SKILL', 0.9, factors);
    }
    if (this.wasEliteCounter(lethal)) {
      return this.result('ELITE_COUNTER', 0.85, factors);
    }

    for (const s of this.shieldBreaks) {
      const ago = t - s;
      if (ago >= 0 && ago <= BALANCE.death.shieldWindow) {
        const confidence = clamp(1 - (ago / BALANCE.death.shieldWindow) * 0.45, 0.55, 0.95);
        return this.result('SHIELD_CHIP', confidence, factors);
      }
    }

    const last = this.snaps[this.snaps.length - 1];
    if (this.wasGreed(last)) {
      return this.result('GREED', 0.85, factors);
    }

    if (last) {
      const occupied = last.sectors.reduce((n, c) => (c > 0 ? n + 1 : n), 0);
      if (occupied >= BALANCE.death.surroundSectors || last.totalNear >= BALANCE.death.surroundCount) {
        return this.result('SURROUNDED', 0.85, factors);
      }
    }

    return this.result('ATTRITION', 0.6, factors);
  }

  private wasGreed(last: Snapshot | undefined): boolean {
    if (!last) return false;
    const d = BALANCE.death;
    for (const s of this.snaps) {
      const nearThreat =
        s.threatDist < d.bossRadius || s.threatDist < d.eliteRadius;
      if (
        s.hpRatio <= d.lowHpRatio &&
        nearThreat && s.threatDot >= d.greedDot
      ) {
        return true;
      }
    }
    return last.hpRatio <= d.lowHpRatio && last.totalNear >= d.surroundCount;
  }

  private wasBossHit(lethal: DamageRecord | null): boolean {
    const s = lethal?.source;
    if (!s) return false;
    if (s.type === 'boss') return true;
    return s.type === 'contact' &&
      (s.kind === 'boss1' || s.kind === 'boss2');
  }

  private wasEliteCounter(lethal: DamageRecord | null): boolean {
    const s = lethal?.source;
    return !!s && s.type === 'contact' && s.kind === 'elite';
  }

  private buildFactors(lethal: DamageRecord | null): string[] {
    const factors: string[] = [];
    if (lethal) factors.push(`致死一击 ${lethal.amount} 伤害，剩余血量 ${(lethal.hpRatio * 100).toFixed(0)}%`);
    const lastHit = this.damages[this.damages.length - 1];
    if (lastHit && lastHit !== lethal) {
      factors.push(`此前连续受击 ${this.damages.length} 次`);
    }
    return factors;
  }

  private result(cause: DeathCauseId, confidence: number, factors: string[]): DeathResult {
    return { cause, label: CAUSE_LABEL[cause], confidence, factors };
  }

  private prune(t: number): void {
    const keep = t - BALANCE.death.logWindow;
    while (this.damages.length > 0 && (this.damages[0] as DamageRecord).t < keep) {
      this.damages.shift();
    }
    while (this.snaps.length > 0 && (this.snaps[0] as Snapshot).t < keep) {
      this.snaps.shift();
    }
    while (this.shieldBreaks.length > 0 && (this.shieldBreaks[0] as number) < keep) {
      this.shieldBreaks.shift();
    }
  }
}
